import { ref, watch, computed } from '@vue/composition-api'
import Vue from 'vue'
import store from '@/store'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { format } from 'date-fns'
import { formatCpf } from '@core/utils/utils'

export default function usePhysicianList() {
  // Use toast
  const toast = useToast()

  const refUserListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'user', sortable: true, sortKey: 'user_id', label: 'Profissional',
    },
    { key: 'profession.label', label: 'Profissão', sortable: true },
    { key: 'email', sortable: true },
    { key: 'cpf', sortable: true },
    { key: 'confirmed_at', label: 'status' },
    { key: 'actions', label: 'Ações' },
  ]
  const perPage = ref(25)
  const totalUsers = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('name')
  const isSortDirDesc = ref(false)
  const situationFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)
  const classificationFilter = ref('')
  const scheduleRestrictionFilter = ref({ label: 'Todos', value: null })

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value ? refUserListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers.value,
    }
  })

  const refetchData = () => {
    refUserListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, situationFilter, planFilter, statusFilter, classificationFilter, scheduleRestrictionFilter], () => {
    refetchData()
  })

  const getReportParams = () => {
    let scheduleRestriction = null
    if (scheduleRestrictionFilter.value?.value) {
      scheduleRestriction = scheduleRestrictionFilter.value.value
    }
    return {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDirection: isSortDirDesc.value ? 'desc' : 'asc',
      situation: situationFilter.value,
      plan: planFilter.value,
      confirmed: statusFilter.value ? statusFilter.value.value : null,
      color: classificationFilter.value ? classificationFilter.value.value : null,
      schedule_restriction: scheduleRestriction,
    }
  }

  const fetchPhysicians = (ctx, callback) => {
    store
      .dispatch('app-physician/fetchPhysicians', getReportParams())
      .then(response => {
        const { data: users, meta } = response.data
        const usrs = users.map(user => {
          const u = { ...user, status: 'active' }
          if (u.cpf) {
            u.cpf = formatCpf(u.cpf, true)
          }
          return u
        })
        callback(usrs)
        totalUsers.value = meta.pagination.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao buscar lista de usuários',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const sendInvite = physician => {
    store
      .dispatch('app-physician/sendInvite', physician)
      .then(() => {
        Vue.swal({
          icon: 'success',
          title: 'Convite Enviado!',
          html: `<strong class="text-capitalize">${physician.name.toLowerCase()}</strong> 
        receberá um convite no email <strong>${physician.email}</strong>.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
      .catch(err => {
        if (err.response && err.response.status === 406) {
          toast({
            component: ToastificationContent,
            props: {
              title: err.response.data.message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          return
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao enviar convite',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const downloadReport = () => {
    store.dispatch('app-physician/downloadReport', getReportParams())
      .then(response => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        const now = format(new Date(), 'yyyy-MM-dd hh:mm:ss')
        link.download = `Medicos_${now}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      })
  }

  const downloadReportPj = () => {
    store.dispatch('app-physician/downloadReportPj', getReportParams())
      .then(response => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        const now = format(new Date(), 'yyyy-MM-dd hh:mm:ss')
        link.download = `Medicos_PJ_${now}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolvePhysicianRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolvePhysicianRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolvePhysicianStatusVariant = status => {
    if (status) return 'success'
    return 'secondary'
  }

  return {
    fetchPhysicians,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,

    resolvePhysicianRoleVariant,
    resolvePhysicianRoleIcon,
    resolvePhysicianStatusVariant,
    refetchData,
    sendInvite,
    downloadReport,
    downloadReportPj,

    // Extra Filters
    situationFilter,
    planFilter,
    classificationFilter,
    statusFilter,
    scheduleRestrictionFilter,
  }
}
